// components/WaitingRoom/WaitingRoomGate.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AuthContext from '../../context/Auth';
import { useWaitingRoom } from '../../hooks/useWaitingRoom';
import Loading from '../Sections/Loading';
import SessionTimer from './SessionTimer';

const SALA_ESPERA_URL = process.env.REACT_APP_WAITING_ROOM_URL || 'http://localhost:3002';
const STUCK_TIMEOUT_MS = 20000; // 20s en Loading -> mostrar mensaje de recarga

// Wrapper interno: solo se renderiza (y ejecuta el hook) cuando el usuario ya está autenticado
const WaitingRoomCheck = ({ children }) => {
    const { loading, admitted, mustWait, sessionRemainingSeconds } = useWaitingRoom();
    const redirectingRef = useRef(false);
    const [stuck, setStuck] = useState(false);

    // Si lleva demasiado tiempo en Loading, mostrar UI de recarga manual
    useEffect(() => {
        if (loading || !admitted) {
            const timer = setTimeout(() => setStuck(true), STUCK_TIMEOUT_MS);
            return () => clearTimeout(timer);
        } else {
            setStuck(false);
        }
    }, [loading, admitted]);

    // ⚠️ Redirigir SOLO si el servidor dijo que hay que esperar (`mustWait`).
    // Con `!admitted` alcanzaba con que /waiting-room/status fallara (401, red)
    // para sacar al usuario de la app y mandarlo a la pantalla "Sesion expirada"
    // de la sala de espera, aunque la cola estuviera desactivada.
    useEffect(() => {
        if (!loading && mustWait && !redirectingRef.current) {
            redirectingRef.current = true;

            // Obtener token de Cognito y redirigir a sala de espera externa
            fetchAuthSession({ forceRefresh: false })
                .then(({ tokens }) => {
                    const idToken = tokens?.idToken?.toString();
                    if (!idToken) {
                        // No hay token: permitir reintento posterior (no dejar atrapado)
                        console.warn('WaitingRoomGate: no hay idToken, reintentando en 2s');
                        redirectingRef.current = false;
                        return;
                    }

                    try {
                        const returnUrl = encodeURIComponent(window.location.href);
                        window.location.href = `${SALA_ESPERA_URL}?returnUrl=${returnUrl}#token=${idToken}`;
                    } catch (err) {
                        console.error('Error redirigiendo a sala de espera:', err);
                        redirectingRef.current = false;
                    }
                })
                .catch((err) => {
                    console.error('Error obteniendo token para sala de espera:', err);
                    redirectingRef.current = false;
                });
        }
    }, [loading, mustWait]);

    // Si está atascado >20s en Loading, mostrar mensaje con botón de recarga
    if ((loading || !admitted) && stuck) {
        return (
            <div style={stuckStyles.container}>
                <div style={stuckStyles.card}>
                    <h2 style={stuckStyles.title}>La carga está tardando más de lo esperado</h2>
                    <p style={stuckStyles.subtitle}>
                        Si llevas mucho tiempo en esta pantalla, intenta recargar.
                    </p>
                    <button onClick={() => window.location.reload()} style={stuckStyles.btn}>
                        Recargar página
                    </button>
                </div>
            </div>
        );
    }

    // Mientras carga o redirige, mostrar loading
    if (loading || !admitted) return <Loading />;

    return (
        <>
            <SessionTimer serverSeconds={sessionRemainingSeconds} />
            {children}
        </>
    );
};

// Gate externo: si el usuario no está autenticado, pasa directo sin waiting room
const WaitingRoomGate = ({ children }) => {
    const { isSignedIn } = useContext(AuthContext);

    // 🧪 SOLO LOCAL — sin sala de espera en pruebas (bypass en entorno local,
    // con o sin login real). Gated por REACT_APP_ENV=local o REACT_APP_LOCAL_AUTH.
    if (process.env.REACT_APP_ENV === 'local' || process.env.REACT_APP_LOCAL_AUTH === 'true') return children;

    // Usuarios no autenticados (páginas públicas) pasan sin cola
    if (!isSignedIn) return children;

    return <WaitingRoomCheck>{children}</WaitingRoomCheck>;
};

const stuckStyles = {
    container: {
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)', padding: 24,
    },
    card: {
        background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 440,
        width: '100%', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    },
    title: { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: '0 0 10px' },
    subtitle: { fontSize: 14, color: '#555', margin: '0 0 20px', lineHeight: 1.5 },
    btn: {
        padding: '10px 24px', borderRadius: 8, border: 'none', background: '#0047bb',
        color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    },
};

export default WaitingRoomGate;
